import { createFileRoute, Outlet } from '@tanstack/react-router'
import Sidebar from '@/components/layout/sidebar/Sidebar'
import MobileTopBar from '@/components/layout/MobileTopBar'
import { useEffect, useRef, useState } from 'react'
import AccessDenied from '@/pages/AccessDenied'
import { ApiError } from '@/lib/axios-client'
import { useNotificationsHub } from '@/features/city-clerk/notification/hooks/useNotificationsHub'
import { NotificationProvider } from '@/features/city-clerk/notification/context/NotificationContext'
import { useNotificationContext } from '@/features/city-clerk/notification/context/useNotificationContext'
import { useAuth } from '@/context/useAuth'
import { Loader } from '@/components/common/Loader'
import StaleBundleNotice from '@/components/common/StaleBundleNotice'
import { isStaleBundleRecoverable } from '@/lib/stale-bundle'

export const Route = createFileRoute('/(app)')({
  component: RouteComponent,
  errorComponent: AppRouteError,
})

function AppRouteError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.isForbidden()) {
    return <AccessDenied />
  }
  if (isStaleBundleRecoverable(error)) {
    return <StaleBundleNotice />
  }
  throw error
}

function AppLayout() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const topBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 640
      setIsMobile(mobile)
      if (!mobile) {
        setIsMobileDrawerOpen(false)
        if (window.innerWidth < 1024) {
          setIsSidebarCollapsed(true)
        }
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobile &&
        isMobileDrawerOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        !topBarRef.current?.contains(event.target as Node)
      ) {
        setIsMobileDrawerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileDrawerOpen, isMobile])

  const { userInfo, loading: isAuthLoading } = useAuth()
  const { isUnreadCountReady } = useNotificationContext()

  useNotificationsHub(userInfo?.userId, userInfo?.cityKey)

  if (isAuthLoading || (userInfo?.userId && !isUnreadCountReady)) {
    return <Loader fullScreen />
  }

  return (
    <div className="flex h-page min-h-0 overflow-hidden bg-(--color-surface)">
      {isMobile && isMobileDrawerOpen && (
        <div
          className="mobile-overlay-animate fixed inset-0 bg-black/40 z-40"
          onClick={() => setIsMobileDrawerOpen(false)}
        />
      )}

      {(!isMobile || isMobileDrawerOpen) && (
        <div ref={sidebarRef}>
          <Sidebar
            isCollapsed={!isMobile && isSidebarCollapsed}
            mobileDropdown={isMobile}
            asideClassName={isMobile ? 'sidebar-dropdown-animate' : ''}
            onToggle={() => {
              if (isMobile) setIsMobileDrawerOpen(false)
              else setIsSidebarCollapsed(!isSidebarCollapsed)
            }}
            onCollapse={() => {
              if (isMobile) setIsMobileDrawerOpen(false)
            }}
          />
        </div>
      )}

      <div
        className={`flex min-w-0 flex-1 flex-col overflow-hidden transition-all duration-300 ${
          isMobile
            ? 'ml-0 pt-16'
            : isSidebarCollapsed
              ? 'ml-16 lg:ml-[60px] rtl:ml-0 rtl:mr-16 rtl:lg:mr-[60px]'
              : 'ml-60 rtl:ml-0 rtl:mr-60'
        }`}
      >
        {isMobile && (
          <div ref={topBarRef}>
            <MobileTopBar
              isMenuOpen={isMobileDrawerOpen}
              onMenuToggle={() => setIsMobileDrawerOpen((prev) => !prev)}
            />
          </div>
        )}
        <div className="app-route-viewport min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

function RouteComponent() {
  return (
    <NotificationProvider>
      <AppLayout />
    </NotificationProvider>
  )
}
